import { useEffect } from 'react';

export default function useOutsideClick(
  ref: React.RefObject<HTMLElement | null>,
  handler: (ev?: Event) => void,
  ignoreRefs?: Array<React.RefObject<HTMLElement | null>>
) {
  useEffect(() => {
    if (!ref) return;

    function isIgnoredTarget(target: Node | null) {
      if (!target) return false;
      if (ref.current && ref.current.contains(target)) return true;
      if (ignoreRefs && ignoreRefs.length) {
        for (const r of ignoreRefs) {
          if (r && r.current && r.current.contains(target)) return true;
        }
      }
      return false;
    }

    function handlePointer(e: Event) {
      const el = ref.current;
      if (!el) return;
      const target = e.target as Node | null;
      if (!target) return;
      if (!isIgnoredTarget(target)) {
        handler(e);
      }
    }

    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') handler(e);
    }

    // Use capture phase to reliably detect outside interactions before other handlers
    document.addEventListener('pointerdown', handlePointer, true);
    document.addEventListener('mousedown', handlePointer, true);
    document.addEventListener('touchstart', handlePointer, true);
    document.addEventListener('click', handlePointer, true);
    document.addEventListener('keydown', handleKey, true);

    return () => {
      document.removeEventListener('pointerdown', handlePointer, true);
      document.removeEventListener('mousedown', handlePointer, true);
      document.removeEventListener('touchstart', handlePointer, true);
      document.removeEventListener('click', handlePointer, true);
      document.removeEventListener('keydown', handleKey, true);
    };
  }, [ref, handler, ignoreRefs]);
}
